/**
 * useConfigurationIdTransition - Handles configuration ID transitions atomically
 *
 * When the server generates a different ID than the client, this hook
 * ensures the selection is updated synchronously with the store update,
 * preventing the "flash" issue where the chart briefly disappears.
 *
 * This replaces the previous callback-based approach which used dynamic
 * imports and had race condition issues.
 */

import { useEffect, useRef } from 'react';
import { useProjectStore } from '~/hooks/useProjectStore';

interface UseConfigurationIdTransitionProps {
  selectedConfigurationId: string | null;
  setSelectedConfigurationId: (id: string | null) => void;
}

/**
 * Hook to handle configuration ID transitions when server generates different ID
 *
 * The store updates lastIdTransition atomically with the configurations array,
 * so by the time this hook updates the selection, the configuration with the
 * new server ID already exists in the array.
 */
export const useConfigurationIdTransition = ({
  selectedConfigurationId,
  setSelectedConfigurationId,
}: UseConfigurationIdTransitionProps): void => {
  // Track the last processed transition to avoid duplicate updates
  const lastProcessedTimestampRef = useRef<number>(0);

  // Subscribe to lastIdTransition from store
  // This selector will cause re-render when lastIdTransition changes
  const lastIdTransition = useProjectStore((state) => state.lastIdTransition);

  useEffect(() => {
    if (!lastIdTransition) return;

    // Skip if we've already processed this transition
    if (lastIdTransition.timestamp <= lastProcessedTimestampRef.current) return;

    // Skip if selection doesn't match the client ID being transitioned
    if (selectedConfigurationId !== lastIdTransition.clientId) return;

    // Mark as processed and update selection to server ID
    lastProcessedTimestampRef.current = lastIdTransition.timestamp;
    setSelectedConfigurationId(lastIdTransition.serverId);
  }, [lastIdTransition, selectedConfigurationId, setSelectedConfigurationId]);
};
